import { AfterContentChecked, ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { marker } from '@common/translate-marker';
import { maxLengthValidator } from '@common/validators/max-length-validator';
import { regexValidator } from '@common/validators/regex-validator';
import { requiredValidator } from '@common/validators/required-validator';
import { TranslateService } from '@ngx-translate/core';
import { DialogCloseResult, DialogRef } from '@progress/kendo-angular-dialog';
import { CodelistService } from '@services/codelist.service';
import { ModalService } from '@services/modal.service';
import { NominatedAgentService } from '@services/nominated-agent.service';
import { ShipmentPoolService } from '@services/shipment-pool.service';
import { ToastService } from '@services/toast.service';
import { CommonService } from 'app/singleton-services/common.service';
import { combineLatest, Observable } from 'rxjs';
import { filter, startWith } from 'rxjs/operators';

@Component({
    selector: 'app-save-cargo-item',
    templateUrl: './save-cargo-item.component.html',
    styleUrls: ['./save-cargo-item.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class SaveCargoItemComponent implements OnInit, AfterContentChecked {
    form: UntypedFormGroup;
    customPortCodelist: any = [];
    manifestId = 0;
    containerized: boolean;
    forTosSystem: string;
    isArrival: boolean;
    isBusy: boolean;
    organizations$: Observable<any>;
    warehouse$: Observable<any>;

    cargoManifestItem: any;
    isEditMode: boolean = false;
    showIMDG: boolean = false;

    cargoManifestMswDGContact: string;
    cargoManifestTypeId: string;

    selectLabel = (item) => {
        if (item.id) return `${item.name}`;
        if (item.localizedCode) return `${item.localizedCode} - ${item.name}`;
        if (item.code) return `${item.code} - ${item.name}`;
    };

    constructor(
        public dialogService: ModalService,
        public activeModal: DialogRef,
        private translateService: TranslateService,
        private cdr: ChangeDetectorRef,
        private toastyService: ToastService,
        public service: NominatedAgentService,
        public codelistService: CodelistService,
        public commonService: CommonService,
        public nominatedAgentService: NominatedAgentService,
        private shipmentPoolService: ShipmentPoolService
    ) {}

    ngOnInit(): void {
        this.form = this.createFormGroup();
        this.organizations$ = this.nominatedAgentService.getOrganizationsForCargoManifest(this.manifestId);
        this.warehouse$ = this.shipmentPoolService.getAllRequestWarehousesPerSystem(this.forTosSystem);
        this.checkEditMode();
        this.customValidations();
        this.trackFormChanges();
    }

    ngAfterContentChecked() {
        this.cdr.detectChanges();
    }

    public get getIsFormDisabled() {
        return this.isBusy || (!this.form.valid && this.form.dirty);
    }

    saveCargoItem() {
        const confirmMessage = this.isEditMode
            ? this.translateService.instant(marker('Are you sure you want to update Cargo List item?'))
            : this.translateService.instant(marker('Are you sure you want to add Cargo List item?'));
        this.dialogService
            .confirm(
                this.translateService.instant(marker('Confirm')),
                confirmMessage,
                this.translateService.instant(marker('YES')),
                this.translateService.instant(marker('No'))
            )
            .pipe(filter((x) => !(x instanceof DialogCloseResult)))
            .subscribe((res) => {
                if (!res) return;
                const body = this.form.getRawValue();

                if (body.sealNumbers && typeof body.sealNumbers === 'string') {
                    body.sealNumbers = body.sealNumbers
                        .split(',')
                        .map((s) => s.trim())
                        .filter((s) => s);
                } else {
                    body.sealNumbers = [];
                }

                this.service.saveCargoManifestItem(body).subscribe(() => {
                    this.toastyService.success(
                        this.translateService.instant(
                            this.isEditMode ? marker('Cargo item updated') : marker('Cargo item added')
                        )
                    );
                    this.activeModal.close(body);
                });
            });
    }

    private checkEditMode() {
        if (this.cargoManifestItem) {
            this.isEditMode = true;
            this.form.patchValue(this.cargoManifestItem);

            if (this.containerized && this.cargoManifestItem.imdgClassId) {
                this.showIMDG = true;
            }

            if (this.cargoManifestItem.seals && this.cargoManifestItem.seals.length > 0) {
                const sealValues = this.cargoManifestItem.seals.map((s) => s.value).join(', ');
                this.form.get('sealNumbers').setValue(sealValues);

                if (this.cargoManifestItem.seals[0].sealType) {
                    this.form.get('sealTypeId').setValue(this.cargoManifestItem.seals[0].sealType);
                }
            }
        }
    }

    private createFormGroup() {
        const form = new UntypedFormGroup({
            id: new UntypedFormControl(),
            bookingNumber: new UntypedFormControl({ value: null, disabled: false }, [maxLengthValidator(50)]),
            billOfLadingNbr: new UntypedFormControl({ value: null, disabled: false }, [
                requiredValidator(),
                maxLengthValidator(50)
            ]),
            containerNumber: new UntypedFormControl(),
            shippingLineOrganizationId: new UntypedFormControl(),
            loadTypeId: new UntypedFormControl(),
            containerIsoCodeId: new UntypedFormControl(),
            containerStatusId: new UntypedFormControl(),
            sealNumbers: new UntypedFormControl(null, [maxLengthValidator(250)]),
            sealTypeId: new UntypedFormControl(),
            containerTareWeight: new UntypedFormControl(),
            cargoNetWeight: new UntypedFormControl(),
            cargoGrossWeight: new UntypedFormControl(null, requiredValidator()),
            vgm: new UntypedFormControl(),
            weightInVacuum: new UntypedFormControl(),
            cargoVolume: new UntypedFormControl(),
            density: new UntypedFormControl(),
            packagingTypeId: new UntypedFormControl(null, requiredValidator()),
            numberOfPackages: new UntypedFormControl(),
            countryOfOriginId: new UntypedFormControl(),
            portOfLoadingId: new UntypedFormControl(),
            portOfDischargeId: new UntypedFormControl(),
            portOfTranshipmentId: new UntypedFormControl(),
            fpodId: new UntypedFormControl(),
            cargoTypePerSystemId: new UntypedFormControl(),
            cargoReference: new UntypedFormControl({ value: null, disabled: false }, [maxLengthValidator(50)]),
            cargoDescription: new UntypedFormControl(null, requiredValidator()),
            cargoShipper: new UntypedFormControl(),
            cargoNotify: new UntypedFormControl(),
            cargoConsignee: new UntypedFormControl(),
            customsDeclarationNumber: new UntypedFormControl(),
            customsDeclarationItemNumber: new UntypedFormControl(),
            taricCode: new UntypedFormControl(),
            imdgClassId: new UntypedFormControl(),
            mswCargoRegulationId: new UntypedFormControl(),
            unNumberId: new UntypedFormControl(),
            packingGroupId: new UntypedFormControl(),
            temperatureFrom: new UntypedFormControl(),
            temperatureTo: new UntypedFormControl(),
            cargoTechnicalDescription: new UntypedFormControl(),
            dgContact: new UntypedFormControl(),
            manifestId: new UntypedFormControl(this.manifestId),
            manifestRowId: new UntypedFormControl(),
            marpolCategoryId: new UntypedFormControl(),
            ibcCodeId: new UntypedFormControl(),
            imsbcCodeId: new UntypedFormControl(),
            flashpoint: new UntypedFormControl(),
            locationOnBoard: new UntypedFormControl(),
            cargoCustomsStatusId: new UntypedFormControl(),
            cargoRealizationTypeId: new UntypedFormControl(),
            inTransit: new UntypedFormControl(),
            customsArticleNumber: new UntypedFormControl({ value: null, disabled: false }, [maxLengthValidator(25)]),
            t2L: new UntypedFormControl(),
            eadARCNumber: new UntypedFormControl({ value: null, disabled: false }, [maxLengthValidator(50)]),
            customsWarehouseTypeId: new UntypedFormControl(),
            warehousePerSystemId: new UntypedFormControl(),
            warehouseTypeId: new UntypedFormControl()
        });
        return form;
    }

    private customValidations() {
        if (this.containerized) {
            if (this.cargoManifestTypeId === 'DCM') {
                this.form.get('bookingNumber').setValidators(requiredValidator());
                this.form.controls['bookingNumber'].updateValueAndValidity();
            }

            this.form.get('cargoNetWeight').setValidators(requiredValidator());
            this.form.controls['cargoNetWeight'].updateValueAndValidity();

            this.form
                .get('containerNumber')
                .setValidators([requiredValidator(), regexValidator('^(?=.{11}$)[A-Z]{4}\\d{7}$')]);
            this.form.controls['containerNumber'].updateValueAndValidity();

            this.form.get('containerIsoCodeId').setValidators(requiredValidator());
            this.form.controls['containerIsoCodeId'].updateValueAndValidity();

            this.form.get('loadTypeId').setValidators(requiredValidator());
            this.form.controls['loadTypeId'].updateValueAndValidity();

            this.form.get('shippingLineOrganizationId').setValidators(requiredValidator());
            this.form.controls['shippingLineOrganizationId'].updateValueAndValidity();
        } else {
            this.form.get('cargoTypePerSystemId').setValidators(requiredValidator());
            this.form.controls['cargoTypePerSystemId'].updateValueAndValidity();
        }

        // RM 42729 - Remove mandatory indicators and validations for Consignee on GUI on Cargo List and Service Request
        /*if (this.cargoManifestTypeId === 'ACM') {
            this.form.get('cargoConsignee').setValidators(requiredValidator());
            this.form.controls['cargoConsignee'].updateValueAndValidity();
        }*/
    }

    private trackFormChanges() {
        this.trackPackagingTypeChanges();

        if (!this.containerized) {
            const dgClassificationBasedControls = [
                'marpolCategoryId',
                'imdgClassId',
                'unNumberId',
                'packingGroupId',
                'ibcCodeId',
                'imsbcCodeId',
                'flashpoint',
                'cargoTechnicalDescription',
                'locationOnBoard',
                'dgContact'
            ];
            const enableAgainControls = ['cargoTechnicalDescription', 'flashpoint', 'locationOnBoard', 'dgContact'];
            dgClassificationBasedControls.forEach((control) => {
                this.form.get(control).disable();
            });
            this.form
                .get('mswCargoRegulationId')
                .valueChanges.pipe(startWith(this.form.get('mswCargoRegulationId').value))
                .subscribe((value) => {
                    if (value) {
                        if (!this.isEditMode) {
                            if (this.cargoManifestMswDGContact) {
                                this.form.get('dgContact').setValue(this.cargoManifestMswDGContact);
                            }
                        } else {
                            if (this.form.get('dgContact').value === null && this.cargoManifestMswDGContact) {
                                this.form.get('dgContact').setValue(this.cargoManifestMswDGContact);
                            }
                        }
                    }
                    if (value === 'MARPOL_ANNEX1') {
                        ['marpolCategoryId', 'dgContact'].forEach((control) => {
                            this.form.get(control).enable();
                        });
                        this.form.get('marpolCategoryId').setValidators(requiredValidator());
                        enableAgainControls.forEach((control) => {
                            this.form.get(control).enable();
                        });
                        this.form.controls['marpolCategoryId'].updateValueAndValidity();
                        ['imdgClassId', 'unNumberId', 'packingGroupId', 'ibcCodeId', 'imsbcCodeId'].forEach(
                            (control) => {
                                this.form.get(control).disable();
                                this.form.get(control).setValue(null);
                            }
                        );
                    } else if (value === 'IMDG') {
                        this.form.get('marpolCategoryId').setValidators(null);
                        enableAgainControls.forEach((control) => {
                            this.form.get(control).enable();
                        });
                        ['imdgClassId', 'unNumberId', 'packingGroupId', 'dgContact'].forEach((control) => {
                            this.form.get(control).enable();
                            this.form.get(control).setValidators(requiredValidator());
                            this.form.controls[control].updateValueAndValidity();
                        });
                        this.form.controls['marpolCategoryId'].updateValueAndValidity();
                        ['marpolCategoryId', 'ibcCodeId', 'imsbcCodeId'].forEach((control) => {
                            this.form.get(control).disable();
                            this.form.get(control).setValue(null);
                        });
                    } else if (value === 'IBC') {
                        enableAgainControls.forEach((control) => {
                            this.form.get(control).enable();
                        });
                        ['marpolCategoryId', 'ibcCodeId', 'dgContact'].forEach((control) => {
                            this.form.get(control).enable();
                            this.form.get(control).setValidators(requiredValidator());
                            this.form.get('dgContact').setValidators(null);
                            this.form.controls[control].updateValueAndValidity();
                        });
                        ['unNumberId', 'packingGroupId', 'imdgClassId', 'imsbcCodeId'].forEach((control) => {
                            this.form.get(control).disable();
                            this.form.get(control).setValue(null);
                        });
                    } else if (value === 'IMSBC') {
                        enableAgainControls.forEach((control) => {
                            this.form.get(control).enable();
                        });
                        ['marpolCategoryId', 'imsbcCodeId', 'dgContact'].forEach((control) => {
                            this.form.get(control).enable();
                            this.form.get(control).setValidators(requiredValidator());
                            this.form.controls[control].updateValueAndValidity();
                        });
                        ['unNumberId', 'packingGroupId', 'imdgClassId', 'ibcCodeId'].forEach((control) => {
                            this.form.get(control).disable();
                            this.form.get(control).setValue(null);
                        });
                    } else if (!value) {
                        dgClassificationBasedControls.forEach((control) => {
                            this.form.get(control).disable();
                            this.form.get(control).setValue(null);
                        });
                    } else {
                        dgClassificationBasedControls.forEach((control) => {
                            this.form.get(control).enable();
                            this.form.get(control).setValidators(null);
                            this.form.controls[control].updateValueAndValidity();
                        });
                    }
                });
        }

        if (this.containerized) {
            const disabledControls = [
                'loadTypeId',
                'cargoDescription',
                'cargoReference',
                'cargoVolume',
                'flashpoint',
                'cargoTechnicalDescription',
                'temperatureFrom',
                'temperatureTo',
                'cargoGrossWeight'
            ];
            const requiredControls = ['cargoNetWeight'];
            this.form
                .get('imdgClassId')
                .valueChanges.pipe(startWith(this.form.get('imdgClassId').value))
                .subscribe((value) => {
                    if (value) {
                        ['unNumberId', 'packingGroupId', 'dgContact'].forEach((control) => {
                            this.form.get(control).setValidators(requiredValidator());
                            this.form.controls[control].updateValueAndValidity();
                        });
                        if (!this.isEditMode) {
                            if (this.cargoManifestMswDGContact) {
                                this.form.get('dgContact').setValue(this.cargoManifestMswDGContact);
                            }
                        } else {
                            if (this.form.get('dgContact').value === null && this.cargoManifestMswDGContact) {
                                this.form.get('dgContact').setValue(this.cargoManifestMswDGContact);
                            }
                        }
                    } else {
                        ['unNumberId', 'packingGroupId', 'dgContact'].forEach((control) => {
                            this.form.get(control).setValidators(null);
                            this.form.controls[control].updateValueAndValidity();
                        });
                    }
                });
            let previousStatus = this.form.get('containerStatusId').value;
            this.form
                .get('containerStatusId')
                .valueChanges.pipe(startWith(this.form.get('containerStatusId').value))
                .subscribe((value) => {
                    const disabled = value === 'E';
                    // valueChanges also re-fires when a sibling handler calls updateValueAndValidity() on this
                    // control; only an actual status change may touch the weights.
                    const statusChanged = value !== previousStatus;
                    // Only the Net weight auto-filled with 0 for Empty is reset when leaving it.
                    const leftEmpty = statusChanged && previousStatus === 'E';
                    previousStatus = value;

                    if (disabled) {
                        disabledControls.forEach((control) => {
                            this.form.get(control).disable();
                        });
                        if (!this.isEditMode && statusChanged) {
                            requiredControls.forEach((control) => {
                                this.form.get(control).setValue('0', { emitEvent: false });
                            });
                        }
                    } else if (value === 'F') {
                        disabledControls.forEach((control) => {
                            this.form.get(control).enable();
                        });
                        if (!this.isEditMode && leftEmpty) {
                            this.clearAutoFilledNetWeight(requiredControls);
                        }
                        this.form.get('loadTypeId').setValidators(requiredValidator());
                        this.form.controls['loadTypeId'].updateValueAndValidity();
                    } else {
                        disabledControls.forEach((control) => {
                            this.form.get(control).enable();
                        });
                        if (!this.isEditMode && leftEmpty) {
                            this.clearAutoFilledNetWeight(requiredControls);
                        }
                        this.form.get('loadTypeId').setValidators(null);
                        this.form.controls['loadTypeId'].updateValueAndValidity();
                    }
                });
        }
        this.form
            .get('containerNumber')
            .valueChanges.pipe(startWith(this.form.get('containerNumber').value))
            .subscribe((value) => {
                if (value) {
                    ['containerStatusId', 'containerIsoCodeId'].forEach((control) => {
                        this.form.get(control).setValidators(requiredValidator());
                        this.form.controls[control].updateValueAndValidity();
                    });
                } else {
                    ['containerStatusId', 'containerIsoCodeId'].forEach((control) => {
                        this.form.get(control).setValidators(null);
                        this.form.controls[control].updateValueAndValidity();
                    });
                }
            });
    }

    private clearAutoFilledNetWeight(controls: string[]) {
        controls.forEach((control) => {
            if (this.form.get(control).value === '0') {
                this.form.get(control).setValue(null, { emitEvent: false });
            }
        });
    }

    private trackPackagingTypeChanges() {
        this.form.get('weightInVacuum').disable();
        this.form.get('density').disable();

        const bulkPackageCodes = ['VL', 'VY', 'VR', 'VO'];

        combineLatest([
            this.form.get('packagingTypeId').valueChanges.pipe(startWith(this.form.get('packagingTypeId').value)),
            this.commonService.getDropdownOptions('CodeListCargoPackagingTypePerSystemForManifest')
        ]).subscribe(([id, packagingTypes]) => {
            const code = (packagingTypes || []).find((t: any) => t.id == id)?.code ?? null;

            if (code === 'VL') {
                this.form.get('weightInVacuum').enable();
            } else {
                this.form.get('weightInVacuum').disable();
                this.form.get('weightInVacuum').setValue(null, { emitEvent: false });
            }

            if (bulkPackageCodes.includes(code)) {
                this.form.get('density').enable();
            } else {
                this.form.get('density').disable();
                this.form.get('density').setValue(null, { emitEvent: false });
            }
        });
    }
}
